import React from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { ArrowLeft } from "lucide-react";

export default function Privacy() {
  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Navbar />
      <main className="max-w-3xl mx-auto px-6 lg:px-8 py-16">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 mb-8" data-testid="privacy-back">
          <ArrowLeft className="h-4 w-4" /> Til forsiden
        </Link>
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Personvern</p>
        <h1 className="mt-2 text-4xl sm:text-5xl font-medium tracking-tight text-slate-900" data-testid="privacy-title">Personvernerklæring</h1>
        <p className="mt-3 text-slate-500">Sist oppdatert: {new Date().toLocaleDateString("nb-NO", { day: "numeric", month: "long", year: "numeric" })}</p>

        <article className="mt-12 prose prose-slate max-w-none space-y-10 text-slate-700 leading-relaxed">
          <section>
            <h2 className="text-2xl font-medium tracking-tight text-slate-900">Behandlingsansvarlig</h2>
            <p className="mt-3"><strong>Sammen Fellesskap</strong>, org.nr. <strong>937 196 504</strong>, er behandlingsansvarlig for personopplysninger som behandles gjennom IdeSetGo. Kontakt: <a href="mailto:hei@idesetgo.no" className="underline text-slate-900">hei@idesetgo.no</a>.</p>
          </section>

          <section>
            <h2 className="text-2xl font-medium tracking-tight text-slate-900">Hvilke opplysninger vi behandler</h2>
            <p className="mt-3"><strong>Vanlige personopplysninger</strong> (behandles så lenge du har aktiv brukerkonto):</p>
            <ul className="mt-3 list-disc pl-6 space-y-2">
              <li><strong>Konto</strong>: Navn og e-postadresse ved registrering.</li>
              <li><strong>Passord</strong>: Lagres kun som en kryptografisk hash (bcrypt) — vi ser aldri klartekst.</li>
              <li><strong>Innhold du selv fyller inn</strong>: Svar og tekst du legger inn for å generere en forretningsplan, samt selve den genererte planen. Dette innholdet sendes til AI-leverandøren (Anthropic) for å produsere resultatet.</li>
              <li><strong>Teknisk data</strong>: IP-adresse, nettleser og enhetstype (til drift og sikkerhet).</li>
              <li><strong>Bruksstatistikk</strong>: Anonymiserte data om hvordan tjenesten brukes — kun hvis du samtykker til statistikk-cookies via cookie-banneret.</li>
            </ul>
            <p className="mt-4"><strong>Regnskaps- og transaksjonsopplysninger</strong> (må lagres lenger etter norsk bokføringslov):</p>
            <ul className="mt-3 list-disc pl-6 space-y-2">
              <li><strong>Ordre- og transaksjonsdata</strong>: Ordre-ID, kvitterings-ID, beløp, dato og samtykke til umiddelbar levering. <strong>Kortnummer lagres aldri hos oss</strong> — betalinger håndteres av Stripe.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-medium tracking-tight text-slate-900">Rettslig grunnlag</h2>
            <ul className="mt-3 list-disc pl-6 space-y-2">
              <li><strong>Avtale</strong> (GDPR art. 6(1)(b)) — for å levere tjenesten du har bestilt.</li>
              <li><strong>Rettslig forpliktelse</strong> (GDPR art. 6(1)(c)) — for oppbevaring av regnskaps- og transaksjonsdata i tråd med bokføringsloven.</li>
              <li><strong>Berettiget interesse</strong> (GDPR art. 6(1)(f)) — for sikkerhet, drift og misbruksforebygging. Du kan når som helst protestere mot behandling basert på berettiget interesse ved å kontakte oss.</li>
              <li><strong>Samtykke</strong> (GDPR art. 6(1)(a)) — for valgfrie statistikk-cookies. Kan trekkes tilbake når som helst.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-medium tracking-tight text-slate-900">Databehandlere og tredjeparter</h2>
            <p className="mt-3">Vi benytter følgende underleverandører for å drive tjenesten:</p>
            <ul className="mt-3 list-disc pl-6 space-y-2">
              <li><strong>Anthropic</strong> — genererer forretningsplaner ut fra innholdet du oppgir.</li>
              <li><strong>Stripe</strong> — betalingsbehandling.</li>
              <li><strong>Resend</strong> — sender kvitteringer og transaksjons-e-poster.</li>
              <li><strong>Emergent</strong> — hosting av applikasjon og database.</li>
              <li><strong>PostHog</strong> — anonymisert bruksstatistikk. Lastes kun etter samtykke via cookie-banneret.</li>
            </ul>
            <p className="mt-3">Enkelte av disse leverandørene er etablert utenfor EØS, og personopplysninger kan derfor behandles utenfor EØS. Eventuelle internasjonale overføringer skal skje i samsvar med GDPR kapittel V og med gyldig overføringsgrunnlag.</p>
          </section>

          <section>
            <h2 className="text-2xl font-medium tracking-tight text-slate-900">Lagringstid</h2>
            <p className="mt-3">Vanlige personopplysninger knyttet til brukerkontoen og lagrede forretningsplaner slettes eller anonymiseres etter 24 måneder uten aktivitet på kontoen. Brukeren kan også be om sletting tidligere ved å kontakte oss på <a href="mailto:hei@idesetgo.no" className="underline text-slate-900">hei@idesetgo.no</a>.</p>
            <p className="mt-3">Opplysninger som vi er lovpålagt å oppbevare, blant annet regnskaps- og transaksjonsdata, beholdes så lenge gjeldende lovgivning krever det (bokføringsloven § 13 krever normalt oppbevaring i minst 5 år).</p>
          </section>

          <section>
            <h2 className="text-2xl font-medium tracking-tight text-slate-900">Cookies</h2>
            <p className="mt-3">Vi bruker to typer cookies:</p>
            <ul className="mt-3 list-disc pl-6 space-y-2">
              <li><strong>Nødvendige</strong>: Autentiserings-cookie (access_token) — kreves for å holde deg innlogget.</li>
              <li><strong>Statistikk (valgfritt)</strong>: Anonymisert produktanalyse via PostHog. Lastes kun etter aktivt samtykke via cookie-banneret.</li>
            </ul>
            <p className="mt-3">Du kan når som helst trekke tilbake eller endre samtykket ditt ved å fjerne <code>planiq_cookie_consent</code> fra nettleserens lagring (cookies/localStorage). Da vises banneret på nytt og du kan velge på nytt.</p>
          </section>

          <section>
            <h2 className="text-2xl font-medium tracking-tight text-slate-900">Dine rettigheter</h2>
            <p className="mt-3">Etter GDPR har du rett til å:</p>
            <ul className="mt-3 list-disc pl-6 space-y-2">
              <li>Få innsyn i hvilke opplysninger vi behandler om deg.</li>
              <li>Rette uriktige opplysninger.</li>
              <li>Slette opplysninger (retten til å bli glemt), med de begrensninger som følger av bokføringsloven for transaksjonsdata.</li>
              <li>Kreve dataportabilitet — få kopi av dine data.</li>
              <li>Trekke tilbake samtykke når som helst.</li>
              <li><strong>Protestere</strong> mot behandling basert på berettiget interesse.</li>
              <li>Klage til Datatilsynet (<a href="https://www.datatilsynet.no" target="_blank" rel="noopener noreferrer" className="underline text-slate-900">datatilsynet.no</a>).</li>
            </ul>
            <p className="mt-3">Send forespørsel til <a href="mailto:hei@idesetgo.no" className="underline text-slate-900">hei@idesetgo.no</a>. Vi svarer normalt innen 30 dager.</p>
          </section>

          <section>
            <h2 className="text-2xl font-medium tracking-tight text-slate-900">Sikkerhet</h2>
            <p className="mt-3">All kommunikasjon skjer over HTTPS. Passord lagres kun som salted hash (bcrypt). Vi begrenser tilgang internt og har rutiner for varsling ved eventuelle sikkerhetshendelser.</p>
          </section>

          <section>
            <h2 className="text-2xl font-medium tracking-tight text-slate-900">Endringer</h2>
            <p className="mt-3">Endringer i denne erklæringen publiseres på denne siden. Ved vesentlige endringer varsler vi deg direkte på e-post.</p>
          </section>
        </article>
      </main>
      <Footer />
    </div>
  );
}
